/**
 * The value of one datapoint and the limits hung on it, as a card or a row of
 * the plant overview shows them.
 *
 * Limits are datapoint names in the datapoint's config (`lat`, `lwt`, `uwt`,
 * `uat`, `setpoint`); their values arrive as datapoints of their own.
 * Subscribes on mount and stops listening on unmount.
 */
import { computed, onMounted, onUnmounted, ref } from 'vue'

import { config, getDeviceIdentifier } from '@/composables/useMqttConnection'
import type { IDatapoint } from '@/interfaces/IDatapoint'
import { thresholdLevel, type Limits } from '@/parameters/threshold'
import { useMqttStore } from '@/stores/mqtt'
import { formatDatapoint } from '@/utils'

export type LimitKey = 'lat' | 'lwt' | 'uwt' | 'uat' | 'setpoint'

const LIMIT_KEYS: ReadonlyArray<LimitKey> = ['lat', 'lwt', 'uwt', 'uat', 'setpoint']

/** The number a datapoint holds, undefined for anything else. */
export function numberOf(datapoint: IDatapoint | undefined): number | undefined {
  const raw = datapoint?.value
  if (raw === undefined || raw === null || typeof raw === 'boolean' || raw === '') return undefined
  const value = Number(raw)
  return Number.isFinite(value) ? value : undefined
}

/** Whether a flag datapoint is set; true and 1 count as set. */
export function isOn(datapoint: IDatapoint | null | undefined): boolean {
  const raw = datapoint?.value
  return raw === true || raw === 1
}

/** The value as text without its unit, dashes while there is none to show. */
export function formatReading(datapoint: IDatapoint): string {
  try {
    return formatDatapoint({ ...datapoint }, false) || '--'
  } catch {
    return '--'
  }
}

export interface Subscriber {
  /** Listens to a datapoint until the component unmounts; ignores an empty name. */
  subscribe(datapoint: string | undefined, handler: (value: IDatapoint) => void): void
}

/** Subscriptions that end with the component. */
export function useDatapointSubscriber(): Subscriber {
  const mqttStore = useMqttStore()
  const unsubscribers: Array<() => void> = []

  onUnmounted(() => unsubscribers.splice(0).forEach((unsubscribe) => unsubscribe()))

  return {
    subscribe(datapoint, handler) {
      if (!datapoint) return
      unsubscribers.push(
        mqttStore.subscribe(`${getDeviceIdentifier()}/datapoints/${datapoint}`, (received) => {
          if (received) handler(received as IDatapoint)
        }),
      )
    },
  }
}

export function useMeasurement(datapoint: string, subscriber = useDatapointSubscriber()) {
  const value = ref({} as IDatapoint)
  const limitValues = ref<Partial<Record<LimitKey, IDatapoint>>>({})

  onMounted(() => {
    subscriber.subscribe(datapoint, (received) => (value.value = received))

    const meta = config.value?.datapoints?.[datapoint]
    for (const key of LIMIT_KEYS) {
      subscriber.subscribe(meta?.[key], (received) => {
        limitValues.value = { ...limitValues.value, [key]: received }
      })
    }
  })

  const limits = computed<Limits>(() => ({
    lat: numberOf(limitValues.value.lat),
    lwt: numberOf(limitValues.value.lwt),
    uwt: numberOf(limitValues.value.uwt),
    uat: numberOf(limitValues.value.uat),
  }))

  const level = computed(() => thresholdLevel(numberOf(value.value), limits.value))

  return { value, limitValues, limits, level }
}
